import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { loadAssessmentData } from '../firebase';

const FormContext = createContext();

export const FormProvider = ({ children }) => {
  const { currentUser, hasValidReferralCode } = useAuth();
  const [assessmentId, setAssessmentId] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedFromFirestore, setHasLoadedFromFirestore] = useState(false);
  const [assessmentStatus, setAssessmentStatus] = useState("in-progress"); // Add assessment status
  
  const [formData, setFormData] = useState({
    // Background & Identity category
    bestCourses: '',
    topSkills: '',
    weakness: '',
    weaknessExamples: '',
    englishLevel: '',
    englishLevelOther: '',
    englishStoryDifference: '',
    needVisa: '',
    visaType: '',
    visaTypeOther: '',
    
    // Industry Interests category
    industryInterests: [],
    industryInterestsOther: '',
    industryInterestsReason: '',
    notInterestedIndustries: '',
    careerCertainty: '',
    
    // Learning Content Interest Pattern category
    interestingTopics: '',
    flowExperience: '',
    assignmentPreference: '',
    
    // Learning Environment Preferences category
    collaborationStyle: '',
    motivation: '',
    feedbackPreference: '',
    groupRole: '',

    // Social Behavior Pattern category
    conflictResolution: '',
    conflictResolutionOther: '',
    networkingStyle: '',
    deadlineResponses: [],
    workQualityUnderPressure: '',

    // Decision-Making Pattern category
    decisionMakingStyle: '',
    attentionToDetail: '',
    personDescription: '',
    vacationPlanning: '',
    riskPreference: '',
    riskType: '',

    // Achievement & Satisfaction Sources category
    achievementExperiences: '',
    successCriteria: [],
    successCriteriaOther: '',
    jobFactors: [],
    jobFactorsOther: '',

    // Adaptability Indicators category
    adaptationTime: '',
    failureResponse: '',
    criticismHandling: '',
    ambiguityComfort: '',
    
    // Personal Information category
    fullName: '',
    wechat: '',
    email: '',
    school: '',
    grade: '',
    major: '',
  });

  // Load saved assessment data when user is authenticated
  useEffect(() => {
    const loadSavedData = async () => {
      if (currentUser && hasValidReferralCode) {
        try {
          setIsLoading(true);
          const result = await loadAssessmentData(currentUser.uid);
          
          if (result.exists) {
            setFormData(prev => ({
              ...prev,
              ...result.responses
            }));
            setAssessmentId(result.id);
            setLastSaved(result.lastAutoSaved || result.lastUpdatedAt);
            
            // Set assessment status if it exists
            if (result.status) {
              setAssessmentStatus(result.status);
              console.log("Assessment status loaded:", result.status);
            }
            
            // Set flag indicating data has been loaded from Firestore
            setHasLoadedFromFirestore(true);
          } else {
            // Even if no data exists, we've completed the loading process
            setHasLoadedFromFirestore(true);
          }
        } catch (error) {
          console.error("Error loading saved assessment:", error);
          // Even on error, we've completed the loading attempt
          setHasLoadedFromFirestore(true);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    loadSavedData();
  }, [currentUser, hasValidReferralCode]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };

  return (
    <FormContext.Provider value={{ 
      formData, 
      handleInputChange, 
      setFormData,
      assessmentId,
      setAssessmentId,
      hasUnsavedChanges,
      setHasUnsavedChanges,
      lastSaved,
      setLastSaved,
      isLoading,
      hasLoadedFromFirestore,
      assessmentStatus,
      setAssessmentStatus
    }}>
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = () => useContext(FormContext);